import Link from "next/link";
import { useState } from "react";
import styles from "./IconButton.module.css";

type IconButtonProps = React.PropsWithChildren<
  React.ButtonHTMLAttributes<HTMLButtonElement>
>;

type IconLinkProps = React.PropsWithChildren<React.ComponentProps<typeof Link>>;

type IconSurfaceProps = React.PropsWithChildren<{ className?: string }>;

function useRipple() {
  const [rippleKey, setRippleKey] = useState(0);
  const [showRipple, setShowRipple] = useState(false);

  return {
    start: () => {
      setRippleKey((prev) => prev + 1);
      setShowRipple(true);
    },
    element: showRipple ? (
      <span
        key={rippleKey}
        className={styles.Ripple}
        aria-hidden="true"
        onAnimationEnd={() => setShowRipple(false)}
      />
    ) : null,
  };
}

export function IconButton({
  children,
  className,
  type = "button",
  onClick,
  ...buttonProps
}: IconButtonProps) {
  const ripple = useRipple();

  return (
    <button
      type={type}
      className={[styles.IconButton, className].filter(Boolean).join(" ")}
      onClick={(event) => {
        ripple.start();
        onClick?.(event);
      }}
      {...buttonProps}
    >
      <span className={styles.HoverBackground} aria-hidden="true" />
      {ripple.element}
      <span className={styles.Content}>{children}</span>
    </button>
  );
}

export function IconLink({
  children,
  className,
  onClick,
  ...linkProps
}: IconLinkProps) {
  const ripple = useRipple();

  return (
    <Link
      className={[styles.IconButton, className].filter(Boolean).join(" ")}
      onClick={(event) => {
        ripple.start();
        onClick?.(event);
      }}
      {...linkProps}
    >
      <span className={styles.HoverBackground} aria-hidden="true" />
      {ripple.element}
      <span className={styles.Content}>{children}</span>
    </Link>
  );
}

export function IconSurface({ children, className }: IconSurfaceProps) {
  return (
    <span className={[styles.IconSurface, className].filter(Boolean).join(" ")}>
      <span className={styles.HoverBackground} aria-hidden="true" />
      <span className={styles.Content}>{children}</span>
    </span>
  );
}
